import { useIonAlert, useIonToast } from '@ionic/react';
import { DuplicateGroupItem, Track } from '../../types/Track';
import {
  getFileName,
  isSeratoRunningDeleteError,
  SERATO_RUNNING_DELETE_MESSAGE,
} from '../../utils/duplicateHelpers';
import { deleteTrack } from './duplicateIpc';
import { DuplicateResolverCore } from './useDuplicateResolver';

export type DeleteOutcome = { successCount: number; errors: string[] };

export type DuplicateGroupMutations = ReturnType<
  typeof useDuplicateGroupMutations
>;

/** Moving files to the recycle bin, and the progress state that goes with it. */
export const useDuplicateGroupMutations = (core: DuplicateResolverCore) => {
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();

  const patchGroup = (groupId: string, patch: Partial<DuplicateGroupItem>) =>
    core.setDuplicateGroupItems((prev) =>
      prev.map((item) => (item.id === groupId ? { ...item, ...patch } : item)),
    );

  /**
   * Deletes tracks one at a time — the delete channel carries no request id,
   * so overlapping calls would interleave their replies.
   */
  const deleteGroupTracks = async (
    groupId: string,
    tracks: Track[],
    timeoutMs: number,
  ): Promise<DeleteOutcome> => {
    let successCount = 0;
    const errors: string[] = [];

    for (const track of tracks) {
      try {
        await deleteTrack(track.id, timeoutMs);
        successCount++;

        if (core.allTracksRef.current) {
          core.allTracksRef.current = core.allTracksRef.current.filter(
            (t) => t.id !== track.id,
          );
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        errors.push(`${getFileName(track.filePath)}: ${message}`);
        core.setDeleteProgress((p) => ({ ...p, failed: p.failed + 1 }));
      } finally {
        core.setDeleteProgress((p) => ({ ...p, completed: p.completed + 1 }));
      }
    }

    return { successCount, errors };
  };

  const beginDeleteProgress = (total: number) =>
    core.setDeleteProgress({
      total,
      completed: 0,
      failed: 0,
      inProgress: true,
    });

  const endDeleteProgress = () =>
    core.setDeleteProgress((p) => ({ ...p, inProgress: false }));

  const reportDeleteFailures = (header: string, errors: string[]) => {
    if (errors.length === 0) return;
    if (errors.every(isSeratoRunningDeleteError)) {
      presentAlert({
        header: SERATO_RUNNING_DELETE_MESSAGE,
        buttons: ['OK'],
      });
      return;
    }
    presentAlert({
      header,
      subHeader: `${errors.length} file(s) stayed where they were`,
      message: errors.join('\n'),
      buttons: ['OK'],
    });
  };

  return {
    presentToast,
    patchGroup,
    deleteGroupTracks,
    beginDeleteProgress,
    endDeleteProgress,
    reportDeleteFailures,
  };
};
